/**
 * Pagina «Automazioni» (SPEC-014.2 Parte B).
 *
 * Il progetto ha già pagato **due volte** il costo di un'automazione il cui
 * stato reale non era visibile da nessuna schermata (F-037, due volte). In
 * entrambi i casi il difetto non era la logica: era che nessuna pagina diceva la
 * verità sullo stato del sistema. Con l'auto-trading in produzione lo stesso
 * difetto non costa una notifica mancata ma un ordine.
 *
 * Da qui le due regole di questa pagina:
 *
 * 1. **«Porta a investimenti?» è la colonna che la giustifica.** È l'unica
 *    informazione che non si ricava da nessun'altra schermata, e va letta prima
 *    di accendere un interruttore.
 * 2. **Uno spento dice sempre perché.** Un innesco inattivo senza ragione manda
 *    a cercare l'interruttore sbagliato — esattamente il fix di F-037.
 */

import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, DomainBadges, Spinner, TableWrap } from '@/ui';
import { AgenticBadge } from '@/features/agent-runs/AgenticBadge';
import { AutomationJournal } from './AutomationJournal';
import { FlowDiagram } from './FlowDiagram';
import { useAutomationsOverview, type LeadsToTrading } from './api';
import './automations.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

const TRADING_LABEL: Record<LeadsToTrading, string> = {
  always: 'Sì',
  conditional: 'Solo con conferma',
  never: 'No',
};

const TRADING_TONE: Record<LeadsToTrading, 'negative' | 'accent' | 'positive'> = {
  // Rosso su «Sì» non è un allarme: è la lettura corretta di «questo innesco
  // può comprare». Il verde sta su «No», che è lo stato innocuo.
  always: 'negative',
  conditional: 'accent',
  never: 'positive',
};

function TradingBadge({ value }: { value: LeadsToTrading }) {
  return <Badge tone={TRADING_TONE[value]}>{TRADING_LABEL[value]}</Badge>;
}

export function AutomationsPage() {
  const overview = useAutomationsOverview();
  const data = overview.data;

  return (
    <div className="automations">
      <header className="page-header">
        <div>
          <h1>Automazioni</h1>
          <p className="page-header__subtitle">
            Tutto ciò che il sistema fa da solo, e cosa può arrivare a un investimento automatico.
            Questa pagina esiste perché due volte un'automazione è sembrata attiva senza esserlo: qui
            uno spento dice sempre <em>perché</em>.
          </p>
        </div>
      </header>

      {overview.isPending ? (
        <Spinner />
      ) : overview.error ? (
        <Alert variant="error" title="Mappa non caricata">
          {errorMessage(overview.error, 'Errore imprevisto')}
        </Alert>
      ) : (
        <>
          <Card title="Mappa degli inneschi" subtitle="Da dove parte il lavoro e dove può arrivare.">
            <FlowDiagram />
          </Card>

          {/* F-168: subito dopo la mappa, prima delle cadenze. La mappa dice cosa
              PUO' succedere; il diario dice cosa E' successo oggi, e la domanda
              «perche' non e' successo niente?» e' quella per cui si apre la pagina. */}
          <AutomationJournal />

          <Card
            title="Inneschi automatici"
            subtitle="Cadenze reali dello scheduler. «Ultima esecuzione» è derivata da ciò che il sistema già registra: dove non lascia traccia, lo dice."
            flush
          >
            <TableWrap>
              <table className="table">
                <thead>
                  <tr>
                    <th>Innesco</th>
                    <th>Cadenza</th>
                    <th>Stato</th>
                    <th>Ultima esecuzione</th>
                    <th>Porta a investimenti?</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.schedules.map((schedule) => (
                    <tr key={schedule.key} className={schedule.enabled ? '' : 'automations__row--off'}>
                      <td>
                        <span className="automations__name">
                          {schedule.label}
                          {/* F-144: il badge sta sul nome, non sulla colonna del
                              trading, perche' dice COME lavora l'innesco e non
                              se compra. Il conteggio evita l'ambiguita' di un
                              innesco per meta' agentico e per meta' no. */}
                          {schedule.agentic && schedule.agentic.markets > 0 && (
                            <>
                              {' '}
                              <AgenticBadge
                                detail={`${schedule.agentic.markets} ${schedule.agentic.markets === 1 ? 'fase' : 'fasi'}${schedule.agentic.standard > 0 ? ` · ${schedule.agentic.standard} standard` : ''}`}
                              />
                            </>
                          )}{' '}
                          {/* SPEC-026 §2.6: quali mondi serve il flusso. Solo
                              «Borsa» finché il motore non esegue su exchange. */}
                          <DomainBadges domains={schedule.domains} />
                        </span>
                        <span className="automations__muted">{schedule.effect}</span>
                        {/* F-185: gli inneschi con un interruttore per utente
                            dicono dove si accendono, invece di lasciarlo cercare. */}
                        {schedule.href && (
                          <Link to={schedule.href} className="automations__muted">
                            Accendi e configura
                          </Link>
                        )}
                      </td>
                      <td className="automations__muted-cell">
                        {schedule.cadence}
                        {schedule.nextRunAt && (
                          <span className="automations__muted">prossima: {formatDateTime(schedule.nextRunAt)}</span>
                        )}
                      </td>
                      <td>
                        <Badge tone={schedule.enabled ? 'positive' : 'neutral'}>
                          {schedule.enabled ? 'attivo' : 'spento'}
                        </Badge>
                        {schedule.disabledReason && (
                          <span className="automations__muted">{schedule.disabledReason}</span>
                        )}
                      </td>
                      <td className="automations__muted-cell">
                        {!schedule.lastRunTracked ? (
                          // «Non disponibile» e non un valore plausibile: questo
                          // innesco non lascia traccia, e mostrare la data di
                          // qualcos'altro misurerebbe un'altra cosa.
                          <em>non disponibile</em>
                        ) : schedule.lastRunAt ? (
                          <>
                            {formatDateTime(schedule.lastRunAt)}
                            {schedule.lastRunOutcome && <span className="automations__muted">{schedule.lastRunOutcome}</span>}
                          </>
                        ) : (
                          <em>mai</em>
                        )}
                      </td>
                      <td>
                        <TradingBadge value={schedule.leadsToTrading} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>

          <Card title="Inneschi manuali" subtitle="I pulsanti che avviano il lavoro, e cosa comportano davvero." flush>
            <TableWrap>
              <table className="table">
                <thead>
                  <tr>
                    <th>Pulsante</th>
                    <th>Dove</th>
                    <th>Cosa comporta</th>
                    <th>Porta a investimenti?</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.manualTriggers.map((trigger) => (
                    <tr key={trigger.key}>
                      <td>
                        <span className="automations__name">
                          {trigger.label} <DomainBadges domains={trigger.domains} />
                        </span>
                      </td>
                      <td className="automations__muted-cell">{trigger.where}</td>
                      <td className="automations__muted-cell">{trigger.effect}</td>
                      <td>
                        <TradingBadge value={trigger.leadsToTrading} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>

          <Card
            title="Interruttori attivi"
            subtitle="Sola lettura. Nessun valore di configurazione sensibile: solo acceso o spento."
          >
            <ul className="automations__switches">
              {data?.switches.map((item) => (
                <li key={item.key}>
                  <div className="automations__switch-head">
                    <Badge tone={item.on ? 'positive' : 'neutral'}>{item.on ? 'acceso' : 'spento'}</Badge>
                    <strong>{item.label}</strong>
                    <code className="automations__code">{item.key}</code>
                  </div>
                  <p className="automations__muted">{item.detail}</p>
                  {item.href ? (
                    <a href={item.href} className="automations__link">
                      Vai dove si cambia →
                    </a>
                  ) : (
                    <span className="automations__muted">
                      Configurazione di server: si cambia nell&apos;ambiente del backend, non da qui.
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
